import * as Phaser from 'phaser';
import { GAME } from '@/config/GameConfig';
import { BootScene } from '@/scenes/BootScene';
import { PreloadScene } from '@/scenes/PreloadScene';
import { CommandHubScene } from '@/scenes/CommandHubScene';
import { BattleFrontScene } from '@/scenes/BattleFrontScene';
import { UIOverlayScene } from '@/scenes/UIOverlayScene';
import { mountDomHud } from '@/ui/DomHud';
import { mountSnnTicker } from '@/ui/SnnTicker';
import { mountTelemetryGraphs } from '@/ui/Telemetry';

async function hasWebGPU(): Promise<boolean> {
  try {
    const gpu = (navigator as Navigator & { gpu?: { requestAdapter(): Promise<unknown> } }).gpu;
    if (!gpu) return false;
    const adapter = await gpu.requestAdapter();
    return !!adapter;
  } catch {
    return false;
  }
}

async function boot(): Promise<void> {
  const gpuOk = await hasWebGPU();

  // Phaser 4 "Caladan" accepts the same `type: AUTO | WEBGL | CANVAS` enum;
  // WEBGPU selection is provided when the Phaser build exposes it, otherwise
  // we let AUTO pick the best available renderer.
  const WEBGPU = (Phaser as unknown as { WEBGPU?: number }).WEBGPU;
  const rendererType = gpuOk && typeof WEBGPU === 'number' ? WEBGPU : Phaser.AUTO;

  const game = new Phaser.Game({
    type: rendererType,
    parent: 'game-root',
    backgroundColor: '#050505',
    width: GAME.WIDTH,
    height: GAME.HEIGHT,
    scale: {
      mode: Phaser.Scale.FIT,
      autoCenter: Phaser.Scale.CENTER_BOTH,
    },
    render: {
      antialias: true,
      pixelArt: false,
      powerPreference: 'high-performance',
    },
    fps: { target: 60, forceSetTimeOut: false },
    scene: [BootScene, PreloadScene, CommandHubScene, BattleFrontScene, UIOverlayScene],
  });

  // Expose for the DOM HUD (scene tabs drive Phaser via this handle).
  window.__phaserGame = game;

  // DOM-side chrome (lives outside Phaser so it renders crisp at any DPR).
  mountDomHud();
  mountSnnTicker();
  mountTelemetryGraphs();
}

boot().catch((err) => {
  console.error('[CandleX] Fatal boot error', err);
});
